import { DragOverlay } from "@dnd-kit/core";
import type { Document, Folder } from "@models";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import {
  DataGridPremium,
  type GridColDef,
  type GridRenderCellParams,
  type GridSortModel,
} from "@mui/x-data-grid-premium";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import type { DocumentsPageItem } from "./Documents.utils";
import { DocumentScopeChip } from "./DocumentScopeChip";
import {
  isDocumentsPageSortField,
  sortDocumentsPageItems,
  type DocumentsPageSortDirection,
  type DocumentsPageSortField,
} from "./DocumentsPageSorting";
import type { DocumentsTableRow } from "./DocumentsTable.types";
import { formatDocumentTableDate } from "./DocumentsTableDates";
import { DocumentsTableDocumentIdentifier } from "./DocumentsTableDocumentIdentifier";
import {
  buildDocumentsTableMovePlan,
  getDocumentsTableItemId,
  getDocumentsTableRowDropTargetId,
  resolveDocumentsTableMoveTarget,
  type DocumentsTableItemId,
} from "./TableDnD/DocumentsTable.dnd";
import {
  DocumentsTableDnDRow,
  DocumentsTableDnDRowContext,
} from "./TableDnD/DocumentsTableDnDRow";
import { DocumentsTableDragOverlay } from "./TableDnD/DocumentsTableDragOverlay";
import type { DocumentsTableMoveController } from "./TableDnD/useDocumentsTableMoveController";
import {
  isDocumentsTableSelectionShortcutEvent,
  isDocumentsTableInteractiveEventTarget,
  shouldIgnoreDocumentsTableKeyboardEvent,
  useDocumentsTableSelection,
} from "./TableDnD/useDocumentsTableSelection";
import { useDocumentsTableRowActions } from "./TableRowActions/useDocumentsTableRowActions";

const DocumentsTableCellText = ({
  children,
  title,
}: {
  children: ReactNode;
  title: string;
}) => (
  <Typography
    component="span"
    noWrap
    sx={{
      display: "block",
      fontSize: "14px",
      minWidth: 0,
    }}
    title={title}
    variant="body2"
  >
    {children}
  </Typography>
);

const renderNameCell = ({
  row,
}: GridRenderCellParams<DocumentsTableRow, string>) => {
  const { item } = row;
  const itemName = row.name || "No name";
  const documentId = item.kind === "document" ? item.id : null;
  const revisionChipLabel =
    item.revisionNumber === null ? null : `R${item.revisionNumber}`;
  const Icon = item.icon;
  const scopeChipLabel = item.kind === "document" ? item.scope : null;
  const routingChipLabel = item.isRouting ? "Routing" : null;

  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        gap: "12px",
        maxWidth: "100%",
        minHeight: 24,
        minWidth: 0,
      }}
    >
      <Icon
        aria-hidden="true"
        data-testid={`documents-table-${item.kind}-icon-${item.id}`}
        fontSize="small"
        sx={{
          alignSelf: "center",
          color: "#6B7280",
          flex: "none",
        }}
      />
      <Box
        sx={{
          alignItems: "stretch",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            data-testid="documents-table-name"
            noWrap
            sx={{
              fontSize: "14px",
              minWidth: 0,
            }}
            title={itemName}
            variant="body2"
          >
            {itemName}
          </Typography>
          {revisionChipLabel && (
            <Tooltip
              describeChild
              placement="top"
              title={`Revision ${item.revisionNumber}`}
            >
              <Chip
                data-testid="documents-table-revision-chip"
                label={revisionChipLabel}
                size="xxs"
                style={{
                  backgroundColor: "#dcfce7",
                  borderColor: "#22c55e",
                }}
                variant="outlined"
                sx={{
                  flex: "none",
                }}
              />
            </Tooltip>
          )}
          {scopeChipLabel && (
            <DocumentScopeChip
              dataTestId="documents-table-scope-chip"
              label={scopeChipLabel}
            />
          )}
          {routingChipLabel && (
            <Tooltip
              describeChild
              placement="top"
              title="This document is waiting for approval"
            >
              <Chip
                data-testid="documents-table-routing-chip"
                label={routingChipLabel}
                size="xxs"
                variant="outlined"
                sx={{
                  bgcolor: "#bbd4ff",
                  borderColor: "#7da4ff",
                  flex: "none",
                }}
              />
            </Tooltip>
          )}
          {documentId && (
            <DocumentsTableDocumentIdentifier
              documentId={documentId}
              documentIdentifier={item.documentIdentifier}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

const renderTextCell = ({
  value = "",
}: GridRenderCellParams<DocumentsTableRow, string>) => (
  <DocumentsTableCellText title={value}>{value}</DocumentsTableCellText>
);

const getColumns = ({
  renderActionsCell,
}: {
  renderActionsCell: (
    params: GridRenderCellParams<DocumentsTableRow, string>
  ) => ReactNode;
}): GridColDef<DocumentsTableRow>[] => [
  {
    field: "name",
    headerName: "Name",
    flex: 38,
    minWidth: 280,
    renderCell: renderNameCell,
  },
  {
    field: "createdBy",
    headerName: "Created by",
    flex: 17,
    minWidth: 150,
    renderCell: renderTextCell,
  },
  {
    field: "createdAt",
    headerName: "Created at",
    flex: 14,
    minWidth: 130,
    renderCell: renderTextCell,
  },
  {
    field: "updatedAt",
    headerName: "Updated at",
    flex: 14,
    minWidth: 160,
    renderCell: renderTextCell,
  },
  {
    field: "type",
    headerName: "Type",
    flex: 12,
    minWidth: 60,
    renderCell: renderTextCell,
  },
  {
    field: "actions",
    headerName: "",
    align: "right",
    disableExport: true,
    filterable: false,
    headerAlign: "right",
    renderCell: renderActionsCell,
    sortable: false,
    width: 56,
  },
];

export const DocumentsTable = ({
  currentDeviceId,
  documentQueryDeviceIds,
  documents,
  folders,
  isLoading,
  items,
  moveController,
  onSelectedItemIdsChange,
  onOpenDocumentInNewTab,
  onOpenItem,
  orgId,
}: {
  currentDeviceId: string | null;
  documentQueryDeviceIds: string[];
  documents: Document[];
  folders: Folder[];
  isLoading: boolean;
  items: DocumentsPageItem[];
  moveController: DocumentsTableMoveController;
  onSelectedItemIdsChange: (itemIds: DocumentsTableItemId[]) => void;
  onOpenDocumentInNewTab: (
    item: Extract<DocumentsPageItem, { kind: "document" }>
  ) => void;
  onOpenItem: (item: DocumentsPageItem) => void;
  orgId: string;
}) => {
  const {
    isInteractionDisabled: areRowActionsDisabled,
    openRowActionContextMenu,
    renderActionsCell: renderRowActionsCell,
    renderRowActionContextMenu,
    renderRowActionDialogs,
  } = useDocumentsTableRowActions({
    currentDeviceId,
    documentQueryDeviceIds,
    documents,
    folders,
    isLoading,
    onOpenDocumentInNewTab,
    onOpenItem,
    orgId,
  });
  const [sortState, setSortState] = useState<{
    direction: DocumentsPageSortDirection;
    field: DocumentsPageSortField;
  } | null>(null);

  const displayedItems = useMemo(() => {
    if (isLoading) {
      return [];
    }

    if (sortState) {
      return sortDocumentsPageItems(
        items,
        sortState.field,
        sortState.direction
      );
    }

    return items;
  }, [isLoading, items, sortState]);

  const rows = useMemo<DocumentsTableRow[]>(
    () =>
      displayedItems.map((item) => ({
        createdAt: formatDocumentTableDate(item.createdAt),
        createdBy: item.createdBy,
        gridId: `${item.kind}-${item.id}`,
        item,
        itemId: getDocumentsTableItemId(item),
        name: item.name.trim(),
        type: item.type,
        updatedAt: formatDocumentTableDate(item.updatedAt),
      })),
    [displayedItems]
  );
  const visibleItemIds = useMemo(() => rows.map((row) => row.itemId), [rows]);
  const selection = useDocumentsTableSelection({
    disabled: areRowActionsDisabled,
    visibleItemIds,
  });
  const isInteractionDisabled =
    areRowActionsDisabled || moveController.isMoving;
  const sortModel = useMemo<GridSortModel>(
    () =>
      sortState ? [{ field: sortState.field, sort: sortState.direction }] : [],
    [sortState]
  );

  const handleSortModelChange = (model: GridSortModel) => {
    const nextSort = model[0];
    const direction = nextSort?.sort;
    const field = nextSort?.field;

    if (
      (direction !== "asc" && direction !== "desc") ||
      !isDocumentsPageSortField(field)
    ) {
      setSortState(null);
      return;
    }

    setSortState({ direction, field });
  };
  const renderActionsCell = useCallback(
    (params: GridRenderCellParams<DocumentsTableRow, string>) =>
      renderRowActionsCell(params, {
        disabled: isInteractionDisabled,
      }),
    [isInteractionDisabled, renderRowActionsCell]
  );
  const columns = useMemo(
    () =>
      getColumns({
        renderActionsCell,
      }),
    [renderActionsCell]
  );

  useEffect(() => {
    onSelectedItemIdsChange(selection.selectedItemIds);
  }, [onSelectedItemIdsChange, selection.selectedItemIds]);

  const handleTableKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (
        isInteractionDisabled ||
        shouldIgnoreDocumentsTableKeyboardEvent(event)
      ) {
        return;
      }

      const isShortcut = isDocumentsTableSelectionShortcutEvent(event);
      const focusedItemId =
        selection.focusedItemId ?? visibleItemIds[0] ?? null;

      if (isShortcut && event.key.toLowerCase() === "a") {
        event.preventDefault();
        selection.selectAllVisible();
        return;
      }

      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const direction = event.key === "ArrowDown" ? "down" : "up";

        if (event.shiftKey) {
          selection.extendSelectionByKeyboard({
            additive: isShortcut,
            direction,
          });
          return;
        }

        selection.moveFocusedRow(direction);
        return;
      }

      if (event.key === " " || event.key === "Spacebar") {
        if (!focusedItemId) {
          return;
        }

        event.preventDefault();

        if (isShortcut) {
          selection.toggleSelection(focusedItemId);
          return;
        }

        selection.replaceSelection(focusedItemId);
        return;
      }

      if (event.key === "Enter") {
        const focusedItem = focusedItemId
          ? moveController.itemsById.get(focusedItemId)
          : null;

        if (!focusedItem) {
          return;
        }

        event.preventDefault();
        onOpenItem(focusedItem);
        return;
      }

      if (event.key === "Escape") {
        if (
          selection.selectedItemIds.length === 0 &&
          !moveController.activeDragItemId
        ) {
          return;
        }

        event.preventDefault();
        moveController.handleDragCancel();
        selection.clearSelection();
        return;
      }
    },
    [
      isInteractionDisabled,
      moveController,
      onOpenItem,
      selection,
      visibleItemIds,
    ]
  );
  const isDropTargetEnabled = useCallback(
    (itemId: DocumentsTableItemId) => {
      if (!moveController.activeDragItemId) {
        return true;
      }

      const target = resolveDocumentsTableMoveTarget({
        foldersById: moveController.foldersById,
        itemsById: moveController.itemsById,
        targetId: getDocumentsTableRowDropTargetId(itemId),
      });

      if (!target) {
        return false;
      }

      return Boolean(
        buildDocumentsTableMovePlan({
          foldersById: moveController.foldersById,
          itemIds: moveController.activeDragSubjectItemIds,
          itemsById: moveController.itemsById,
          target,
        })
      );
    },
    [
      moveController.activeDragItemId,
      moveController.activeDragSubjectItemIds,
      moveController.foldersById,
      moveController.itemsById,
    ]
  );
  const handleRowContextMenu = useCallback(
    (row: DocumentsTableRow, event: MouseEvent<HTMLDivElement>) => {
      if (
        isInteractionDisabled ||
        isDocumentsTableInteractiveEventTarget(event.target)
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      selection.replaceSelection(row.itemId);
      openRowActionContextMenu(row.item, {
        left: event.clientX,
        top: event.clientY,
      });
    },
    [isInteractionDisabled, openRowActionContextMenu, selection]
  );
  const rowDndContextValue = useMemo(
    () => ({
      activeDragItemId: moveController.activeDragItemId,
      disabled: isInteractionDisabled,
      isDropTargetEnabled,
      onRowContextMenu: handleRowContextMenu,
      selectedItemIds: selection.selectedItemIdSet,
    }),
    [
      handleRowContextMenu,
      isDropTargetEnabled,
      isInteractionDisabled,
      moveController.activeDragItemId,
      selection.selectedItemIdSet,
    ]
  );
  const activeDragOverlay = useMemo(() => {
    const { activeDragItemId, activeDragSubjectItemIds, itemsById } =
      moveController;

    if (!activeDragItemId) {
      return null;
    }

    const dragPreviewItems = [
      activeDragItemId,
      ...activeDragSubjectItemIds.filter(
        (itemId) => itemId !== activeDragItemId
      ),
    ]
      .map((itemId) => itemsById.get(itemId) ?? null)
      .filter((item): item is DocumentsPageItem => item !== null);

    if (dragPreviewItems.length === 0) {
      return null;
    }

    return (
      <DocumentsTableDragOverlay
        items={dragPreviewItems}
        totalCount={dragPreviewItems.length}
      />
    );
  }, [
    moveController.activeDragItemId,
    moveController.activeDragSubjectItemIds,
    moveController.itemsById,
  ]);
  const rowActionDialogs = renderRowActionDialogs({
    disabled: isInteractionDisabled,
  });
  const rowActionContextMenu = renderRowActionContextMenu({
    disabled: isInteractionDisabled,
  });

  return (
    <Box
      data-testid="documents-table-container"
      onKeyDown={handleTableKeyDown}
      tabIndex={0}
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        minHeight: 0,
        outline: "none",
        width: "100%",
        "&:focus, &:focus-visible": {
          outline: "none",
        },
      }}
    >
      <DragOverlay zIndex={1400}>{activeDragOverlay}</DragOverlay>
      <DocumentsTableDnDRowContext.Provider value={rowDndContextValue}>
        <Box
          data-testid="documents-table-body-drop-target"
          sx={{
            display: "flex",
            flex: 1,
            minHeight: 0,
          }}
        >
          <DataGridPremium
            aria-label="Documents"
            rows={rows}
            columns={columns}
            getRowId={(row) => row.gridId}
            onRowClick={(params, event) => {
              if (isLoading) {
                return;
              }

              selection.handleRowClickSelection(
                params.row.itemId,
                event as MouseEvent
              );

              if (
                !event.shiftKey &&
                !isDocumentsTableSelectionShortcutEvent(event)
              ) {
                onOpenItem(params.row.item);
              }
            }}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            sortingMode="server"
            sortingOrder={["asc", "desc"]}
            loading={isLoading}
            columnHeaderHeight={42}
            sx={{
              border: "none",
              boxShadow: "none",
              flex: 1,
              lineHeight: 2,
              fontSize: "14px",
              minHeight: 0,
              outline: "none",
              "&.MuiDataGrid-root": {
                border: "none",
                boxShadow: "none",
                outline: "none",
              },
              ".MuiDataGrid-main": {
                border: "none",
                outline: "none",
              },
              ".MuiDataGrid-withBorderColor": {
                borderColor: "transparent",
              },
              "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                py: "9px",
              },
              ".MuiDataGrid-columnHeaders, .MuiDataGrid-cell": {
                borderBottom: "none",
              },
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
              },
              ".MuiDataGrid-columnHeader": {
                backgroundColor: "#FFFFFF",
                borderBottom: "none",
                outline: "none",
                padding: "9px 10px",
              },
              ".MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within, .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-row:focus, .MuiDataGrid-row:focus-within":
                {
                  outline: "none",
                },
              ".MuiDataGrid-cellContent, .MuiDataGrid-columnHeaderTitle": {
                textOverflow: "clip",
              },
              ".MuiDataGrid-virtualScroller": {
                overflowX: "hidden",
              },
              "& .MuiDataGrid-filler": {
                borderBottom: "none",
              },
              ".MuiDataGrid-cell.MuiDataGrid-cellEmpty": {
                border: "none",
              },
              ".MuiDataGrid-container--top [role=row]": {
                backgroundColor: "#FFFFFF",
              },
              ".MuiDataGrid-row": {
                borderBottom: "none",
                cursor: "pointer",
                outline: "none",
              },
              ".MuiDataGrid-row.documents-table-row--selected": {
                backgroundColor: "#EEF5FF",
              },
              ".MuiDataGrid-row.documents-table-row--drop-target": {
                backgroundColor: "#DBEAFE",
              },
              ".MuiDataGrid-cell": {
                alignItems: "center",
                borderBottom: "none",
                display: "flex",
                outline: "none",
                px: "10px",
              },
            }}
            ignoreValueFormatterDuringExport
            getRowHeight={() => "auto"}
            disableColumnMenu
            disableRowSelectionOnClick
            hideFooterRowCount
            hideFooter
            scrollbarSize={0}
            disableColumnReorder
            localeText={{ noRowsLabel: "No documents or folders" }}
            slots={{
              row: DocumentsTableDnDRow,
            }}
            slotProps={{
              loadingOverlay: {
                noRowsVariant: "skeleton",
                variant: "skeleton",
              },
            }}
          />
        </Box>
      </DocumentsTableDnDRowContext.Provider>

      {rowActionDialogs}
      {rowActionContextMenu}
    </Box>
  );
};
